import { Flags } from '@fullstory/client-api';
import { NumericAggregationMethod } from '@fullstory/proto/fs/api/metrics';
import {
  ComputeFunnelMetricsRequest,
  ComputeMetricResponse,
  FunnelMetricsServicePublic,
  MetricsServicePublic,
} from '@fullstory/proto/fs/services/alexa';
import {
  AppBackendPublic,
  CreateCardRequest,
  CreateFromVirtualDashboardRequest,
  Dashboard,
  DashboardsPublic,
  DuplicateCardRequest,
  GetAllCardsRequest,
  GetAllDashboardsRequest,
  GetCardRequest,
  MarkDashboardViewedRequest,
  SpacesPublic,
  StarDashboardRequest,
  TransferDashboardOwnerRequest,
  UpdateCardRequest,
} from '@fullstory/proto/fs/services/dx-objects';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  ActionType,
  createAction,
  createGRPCAction,
  RequestActionType,
} from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { hashObject } from '@fullstory/util/hash';
import { UID } from '@fullstory/util/uid';
import { LegacyMetricsRequestBody } from '../metrics';
import { metricsRequestBodyToLegacyComputeMetricRequest } from '../metrics/utils';
import { SearchCacheKey } from '../searchMeta';
import { CardRequestTypes, ConversionFunnelCardRequestTypes, DashboardRequestTypes } from './types';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

export const appCacheRequestMetrics = (
  request: ComputeFunnelMetricsRequest,
  searchCacheKey: SearchCacheKey,
) =>
  createGRPCAction<typeof FunnelMetricsServicePublic.ComputeFunnelMetrics>({
    cacheKey: url => `${url}:${hashObject(request)}:${hashObject(searchCacheKey)}`,
  })(
    {
      type: ConversionFunnelCardRequestTypes.AppCacheRequest,
      grpcFunction: FunnelMetricsServicePublic.ComputeFunnelMetrics,
      body: request,
      force: request?.cacheOptions?.skipCache,
      settings: {
        batchOptions: {
          maxOutgoingRequests: 3,
          id: FunnelMetricsServicePublic.ComputeFunnelMetricsEndpoint,
        },
      },
      readWrite: 'write',
    },
    ConversionFunnelCardRequestTypes.AppCacheSuccess,
    ConversionFunnelCardRequestTypes.AppCacheFailure,
  );

const cacheKeysToInvalidate = (dashboardId?: int64): string[] => {
  const getAllEndpoint = DashboardsPublic.GetAllDashboardsEndpoint;
  const getInitialEndpoint = DashboardsPublic.GetInitialDashboardsEndpoint;
  const cacheKeys = [
    defaultCacheKey(getAllEndpoint, undefined),
    defaultCacheKey(getInitialEndpoint, {}),
  ];
  if (dashboardId) {
    const getEndpoint = DashboardsPublic.GetDashboardEndpoint;
    cacheKeys.push(defaultCacheKey(getEndpoint, dashboardId));
  }
  return cacheKeys;
};

const cardCacheKeysToInvalidate = (
  dashboardId: int64,
  cardId: int64,
  orgId: string,
  flags: Flags,
) => {
  const getAllEndpoint = DashboardsPublic.GetAllCardsEndpoint;
  const getEndpoint = DashboardsPublic.GetCardEndpoint;

  const getCardsRequest: GetAllCardsRequest = {
    dashboardId,
    orgId,
  };
  const getCardRequest: GetCardRequest = {
    orgId,
    dashboardId,
    cardId,
    overlay: null,
  };

  const cacheKeys = [
    defaultCacheKey(getAllEndpoint, getCardsRequest),
    defaultCacheKey(getEndpoint, getCardRequest),
  ];

  cacheKeys.push(
    defaultCacheKey(AppBackendPublic.GetDashboardDetailViewEndpoint, {
      dashboardId,
      orgId,
      overlay: null,
    }),
  );

  return cacheKeys;
};

export const computeRevenueMetricRequest = (
  request: LegacyMetricsRequestBody,
  searchCacheKey: SearchCacheKey,
  uid?: UID,
) => {
  const metricsRequest = metricsRequestBodyToLegacyComputeMetricRequest(request);
  return createGRPCAction<typeof MetricsServicePublic.ComputeMetric, number>({
    cacheKey: (url: string) => {
      const { cacheOptions, ...requestCacheData } = metricsRequest;
      return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
    },
    modify: (resp: ComputeMetricResponse): number => {
      if (resp?.data?.table?.length == 1 && resp.data.table[0]) {
        return resp.data.table[0].value;
      } else {
        let method: string = '';
        if (
          metricsRequest.computation?.metric?.single?.aggregation?.numeric?.aggregation ===
          NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_SUM
        ) {
          method = 'Total Revenue';
        } else if (
          metricsRequest.computation?.metric?.single?.aggregation?.numeric?.aggregation ===
          NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_AVERAGE
        ) {
          method = 'Avg. Order Value';
        }
        throw new Error(`Could not compute ${method}. Metric API response was invalid.`);
      }
    },
    uid,
  })(
    {
      type: ConversionFunnelCardRequestTypes.AppCacheRequest,
      grpcFunction: MetricsServicePublic.ComputeMetric,
      body: metricsRequest,
      force: metricsRequest?.cacheOptions?.skipCache,
      readWrite: 'write',
    },
    ConversionFunnelCardRequestTypes.AppCacheSuccess,
    ConversionFunnelCardRequestTypes.AppCacheFailure,
  );
};

export const DashboardRequestActions = {
  createDashboard: (dashboard: Partial<Dashboard>, orgId: string, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.CreateDashboard>()(
      {
        type: DashboardRequestTypes.CreateDashboardRequest,
        body: {
          orgId,
          dashboard: dashboard as Dashboard,
        },
        force: true,
        grpcFunction: DashboardsPublic.CreateDashboard,
        readWrite: 'write',
      },
      DashboardRequestTypes.CreateDashboardSuccess,
      DashboardRequestTypes.CreateDashboardFailure,
    ),
  duplicateDashboard: (id: int64, orgId: string, visibility: Visibility, spaceId?: string) =>
    createGRPCAction<
      typeof DashboardsPublic.DuplicateDashboard | typeof SpacesPublic.DuplicateSpaceDashboard
    >()(
      {
        type: DashboardRequestTypes.DuplicateDashboardRequest,
        body: {
          orgId,
          dashboardId: id,
          id,
          visibility,
          spaceId,
        },
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(),
        grpcFunction: SpacesPublic.DuplicateSpaceDashboard,
        readWrite: 'write',
      },
      DashboardRequestTypes.DuplicateDashboardSuccess,
      DashboardRequestTypes.DuplicateDashboardFailure,
    ),
  updateDashboard: (dashboard: Dashboard, orgId: string, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.UpdateDashboard>()(
      {
        type: DashboardRequestTypes.UpdateDashboardRequest,
        body: {
          orgId,
          dashboard,
        },
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(dashboard.id),
        grpcFunction: DashboardsPublic.UpdateDashboard,
        readWrite: 'write',
      },
      DashboardRequestTypes.UpdateDashboardSuccess,
      DashboardRequestTypes.UpdateDashboardFailure,
    ),
  deleteDashboard: (id: int64, orgId: string, flags: Flags) => {
    const getEndpoint = DashboardsPublic.GetDashboardEndpoint;
    const getAllEndpoint = DashboardsPublic.GetAllDashboardsEndpoint;
    return createGRPCAction<typeof DashboardsPublic.DeleteDashboard>()(
      {
        type: DashboardRequestTypes.DeleteDashboardRequest,
        body: {
          orgId,
          dashboardId: id,
        },
        invalidateCacheKey: [
          defaultCacheKey(getEndpoint, id),
          defaultCacheKey(getAllEndpoint, undefined),
        ],
        grpcFunction: DashboardsPublic.DeleteDashboard,
        readWrite: 'write',
      },
      DashboardRequestTypes.DeleteDashboardSuccess,
      DashboardRequestTypes.DeleteDashboardFailure,
    );
  },
  bulkDeleteDashboards: (ids: int64[], orgId: string) => {
    const getEndpoint = DashboardsPublic.GetDashboardEndpoint;
    const getAllEndpoint = DashboardsPublic.GetAllDashboardsEndpoint;
    return createGRPCAction<typeof DashboardsPublic.BulkDeleteDashboards>()(
      {
        type: DashboardRequestTypes.BulkDeleteDashboardsRequest,
        body: {
          orgId,
          dashboards: ids.map(id => ({ orgId, dashboardId: id })),
        },
        invalidateCacheKey: [
          ...ids.map(id => defaultCacheKey(getEndpoint, id)),
          defaultCacheKey(getAllEndpoint, undefined),
        ],
        grpcFunction: DashboardsPublic.BulkDeleteDashboards,
        readWrite: 'write',
      },
      DashboardRequestTypes.BulkDeleteDashboardsSuccess,
      DashboardRequestTypes.BulkDeleteDashboardsFailure,
    );
  },
  fetchDashboard: (id: int64, orgId: string, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.GetDashboard>()(
      {
        type: DashboardRequestTypes.FetchDashboardRequest,
        body: {
          orgId,
          dashboardId: id,
        },
        grpcFunction: DashboardsPublic.GetDashboard,
        readWrite: 'read',
      },
      DashboardRequestTypes.FetchDashboardSuccess,
      DashboardRequestTypes.FetchDashboardFailure,
    ),
  fetchAllDashboard: (
    body: GetAllDashboardsRequest,
    // Webber service require caller to provide email, while dx-objects pulls the email from the session
    // Delete this once webber service is deprecated
    email: string,
    flags: Flags,
    force?: boolean,
  ) =>
    createGRPCAction<typeof DashboardsPublic.GetAllDashboards>()(
      {
        type: DashboardRequestTypes.FetchAllDashboardRequest,
        force,
        grpcFunction: DashboardsPublic.GetAllDashboards,
        body,
        readWrite: 'read',
      },
      DashboardRequestTypes.FetchAllDashboardSuccess,
      DashboardRequestTypes.FetchAllDashboardFailure,
    ),
  fetchInitialDashboards: (orgId: string, flags: Flags, force?: boolean) =>
    createGRPCAction<typeof DashboardsPublic.GetInitialDashboards>()(
      {
        type: DashboardRequestTypes.FetchInitialDashboardsRequest,
        force,
        grpcFunction: DashboardsPublic.GetInitialDashboards,
        body: {
          orgId,
        },
        readWrite: 'read',
      },
      DashboardRequestTypes.FetchInitialDashboardsSuccess,
      DashboardRequestTypes.FetchInitialDashboardsFailure,
    ),
  starDashboard: (req: StarDashboardRequest, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.StarDashboard>()(
      {
        type: DashboardRequestTypes.StarDashboardRequest,
        body: req,
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(req.dashboardId),
        grpcFunction: DashboardsPublic.StarDashboard,
        readWrite: 'write',
      },
      DashboardRequestTypes.StarDashboardSuccess,
      DashboardRequestTypes.StarDashboardFailure,
    ),
  markDashboardViewed: (req: MarkDashboardViewedRequest, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.MarkDashboardViewed>()(
      {
        type: DashboardRequestTypes.MarkDashboardViewedRequest,
        body: req,
        grpcFunction: DashboardsPublic.MarkDashboardViewed,
        readWrite: 'write',
      },
      DashboardRequestTypes.MarkDashboardViewedSuccess,
      DashboardRequestTypes.MarkDashboardViewedFailure,
    ),
  getAccess: (req: CanAccessRequest, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.GetDashboardsAccess>()(
      {
        type: DashboardRequestTypes.DashboardAccessRequest,
        body: req,
        force: true,
        grpcFunction: DashboardsPublic.GetDashboardsAccess,
        readWrite: 'write',
      },
      DashboardRequestTypes.DashboardAccessSuccess,
      DashboardRequestTypes.DashboardAccessFailure,
    ),
  transferOwner: (req: TransferDashboardOwnerRequest, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.TransferDashboardOwner>()(
      {
        type: DashboardRequestTypes.DashboardTransferOwnerRequest,
        body: req,
        force: true,
        grpcFunction: DashboardsPublic.TransferDashboardOwner,
        readWrite: 'write',
      },
      DashboardRequestTypes.DashboardTransferOwnerSuccess,
      DashboardRequestTypes.DashboardTransferOwnerFailure,
    ),
  createFromVirtualDashboard: (req: CreateFromVirtualDashboardRequest) =>
    createGRPCAction<typeof SpacesPublic.CreateInSpaceFromVirtualDashboard>()(
      {
        type: DashboardRequestTypes.CreateFromVirtualDashboardRequest,
        body: req,
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(),
        grpcFunction: SpacesPublic.CreateInSpaceFromVirtualDashboard,
        readWrite: 'write',
      },
      DashboardRequestTypes.CreateFromVirtualDashboardSuccess,
      DashboardRequestTypes.CreateFromVirtualDashboardFailure,
    ),
};

export const DashboardActionsAfterReactQuery = {
  updateDashboardAfterCardMove: (dashboard: Dashboard) =>
    createAction({
      type: DashboardRequestTypes.UpdateDashboardAfterCardMove,
      dashboard,
    }),
};

export const CardRequestActions = {
  createCard: (body: CreateCardRequest, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.CreateCard>()(
      {
        type: CardRequestTypes.CreateCardRequest,
        force: true,
        body,
        invalidateCacheKey: cacheKeysToInvalidate(body.dashboardId),
        grpcFunction: DashboardsPublic.CreateCard,
        readWrite: 'write',
      },
      CardRequestTypes.CreateCardSuccess,
      CardRequestTypes.CreateCardFailure,
    ),
  updateCard: (body: UpdateCardRequest, flags: Flags) =>
    createGRPCAction<typeof DashboardsPublic.UpdateCard>()(
      {
        type: CardRequestTypes.UpdateCardRequest,
        body,
        invalidateCacheKey: cacheKeysToInvalidate(body.dashboardId),
        grpcFunction: DashboardsPublic.UpdateCard,
        readWrite: 'write',
      },
      CardRequestTypes.UpdateCardSuccess,
      CardRequestTypes.UpdateCardFailure,
    ),
  deleteCard: (dashboardId: int64, cardId: int64, flags: Flags, orgId: string) =>
    createGRPCAction<typeof DashboardsPublic.DeleteCard>()(
      {
        type: CardRequestTypes.DeleteCardRequest,
        body: {
          orgId,
          cardId,
          dashboardId,
        },
        grpcFunction: DashboardsPublic.DeleteCard,
        readWrite: 'write',
      },
      CardRequestTypes.DeleteCardSuccess,
      CardRequestTypes.DeleteCardFailure,
    ),
  fetchCard: (dashboardId: int64, cardId: int64, orgId: string, flags: Flags, force?: boolean) =>
    createGRPCAction<typeof DashboardsPublic.GetCard>()(
      {
        type: CardRequestTypes.FetchSingleCardRequest,
        body: {
          orgId,
          cardId,
          dashboardId,
          overlay: null,
        },
        force,
        grpcFunction: DashboardsPublic.GetCard,
        readWrite: 'write',
      },
      CardRequestTypes.FetchSingleCardSuccess,
      CardRequestTypes.FetchSingleCardFailure,
    ),
  duplicateCard: (body: DuplicateCardRequest, flags: Flags) =>
    createGRPCAction<
      typeof DashboardsPublic.DuplicateCard | typeof SpacesPublic.DuplicateSpaceDashboardCard
    >()(
      {
        type: CardRequestTypes.DuplicateCardRequest,
        force: true,
        body,
        invalidateCacheKey: [
          ...cardCacheKeysToInvalidate(body.dashboardId, body.cardId, body.orgId, flags),
          ...cacheKeysToInvalidate(body.dashboardId),
        ],
        grpcFunction: SpacesPublic.DuplicateSpaceDashboardCard,
        readWrite: 'write',
      },
      CardRequestTypes.DuplicateCardSuccess,
      CardRequestTypes.DuplicateCardFailure,
    ),
};

export type DashboardActions =
  | RequestActionType<typeof DashboardRequestActions>
  | ActionType<typeof DashboardActionsAfterReactQuery>;
export type CardRequestActions = RequestActionType<typeof CardRequestActions>;
